import { OutlinedInput } from '@mui/material'
import { compact } from 'lodash-es'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import FluentSearch24Filled from '~icons/fluent/search-24-filled'
import { FitToggle } from 'src/components/Common/FitToggle'
import Text from 'src/components/ui/Text'
import type { TaskSummary } from 'src/services/apollo/generated/graphql'
import { executeDeleteGenerationTask } from 'src/services/apollo/task'
import {
  type DownloadState,
  downloadTaskSummaryMedias,
} from 'src/utils/download'
import { useQs } from 'src/utils/qs'
import { track } from 'src/utils/tracker'
import type { BaseCursorPagingRef } from '../CursorPaging/BaseCursorPaging'
import { TaskList } from '../TaskList/TaskList'
import { UIButton } from '../ui/Button'
import { useEditItems } from './EditItems'

const ProfileTaskItemList: FC<{ userId: string }> = ({ userId }) => {
  const { t } = useTranslation(['action', 'user'])
  const [, setQs] = useQs()
  const [currentPageTasks, setCurrentPageTasks] = useState<TaskSummary[]>([])
  const selectedTaskCache = useRef(new Map<string, TaskSummary>())
  const [downloadState, setDownloadState] = useState<DownloadState>({
    progress: 0,
    total: 0,
  })

  const [{ loading: downloading }, triggerDownload] = useAsyncFn(
    async (selectedIds: string[]) => {
      try {
        await downloadTaskSummaryMedias(
          compact(selectedIds.map(id => selectedTaskCache.current.get(id))),
          setDownloadState,
        )
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : String(err))
        setDownloadState({ progress: 0, total: 0 })
      }
    },
    [],
  )

  const pagingRef = useRef<BaseCursorPagingRef>(null)

  const editState = useEditItems({
    actionButtonName: t('action:manage'),
    deleteItem: taskId => executeDeleteGenerationTask({ taskId }),
    afterDelete: () => {
      pagingRef.current?.mutate()
    },
    dialogTitle: t('user:profile-sub-tab.tasks.manage.confirm.title'),
    dialogContent: t('user:profile-sub-tab.tasks.manage.confirm.content'),
    renderEditingExtras: ({
      buttonSize,
      selectedItemIds,
      setSelectedItemIds,
    }) => {
      const handleSelectAll = () => {
        currentPageTasks.forEach(task => {
          selectedTaskCache.current.set(task.id, task)
        })

        const newSelectedItems = Array.from(
          new Set([...selectedItemIds, ...currentPageTasks.map(t => t.id)]),
        )
        setSelectedItemIds(newSelectedItems)
      }

      return (
        <>
          <UIButton
            variant="outlined"
            size={buttonSize}
            onPress={handleSelectAll}
          >
            <Text i18nKey="action:select-all" />
          </UIButton>

          <UIButton
            variant="filled"
            busy={downloading && downloadState.progress === 0}
            disabled={selectedItemIds.length === 0 || downloading}
            className="capitalize"
            onPress={() => triggerDownload(selectedItemIds)}
            size={buttonSize}
          >
            {downloading ? (
              `${downloadState.progress} / ${downloadState.total}`
            ) : (
              <Text i18nKey="action:download" />
            )}
          </UIButton>
        </>
      )
    },
  })

  const onSearch = (keyword: string) => {
    setQs({
      keyword: keyword || null,
      before: null,
      after: null,
    })
    track('task_search_commit', {
      scene: 'profile',
    })
  }

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex w-full flex-col gap-3 sm:max-w-full sm:flex-row sm:items-center sm:gap-4">
          <OutlinedInput
            className="w-full sm:max-w-72"
            size="small"
            placeholder={t('common:search')}
            startAdornment={
              <FluentSearch24Filled className="mr-2 size-6 text-neutral-500" />
            }
            onKeyDown={e => {
              if (e.key === 'Enter') onSearch(e.currentTarget.value)
            }}
            onBlur={e => {
              onSearch(e.currentTarget.value)
            }}
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 sm:flex-nowrap">
          <FitToggle
            localStorageKey="task-preview-card-fit"
            iconClassName="size-7"
          />
          {editState.editItemsAction}
        </div>
      </div>

      <TaskList
        pagingRef={pagingRef}
        userId={userId}
        editing={editState.editing}
        selectedItems={editState.selectedItems}
        toggleSelected={(id, task) => {
          const selected = editState.selectedItems.includes(id)
          if (selected) {
            selectedTaskCache.current.delete(id)
          } else if (task.mediaId) {
            selectedTaskCache.current.set(id, task)
          }
          editState.toggleSelected(id)
        }}
        onDataChange={data => {
          setCurrentPageTasks(data ?? [])
        }}
      />
    </div>
  )
}

export default ProfileTaskItemList
